import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Menu,
  MenuItem,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import CustomLabel from "components/CustomLabel";
import { useTheme } from "@mui/material/styles";
import { esES } from "@mui/material/locale";
import CustomAutocomplete from "components/mui/autocomplete";
import { GetTagsReport } from "../logic/TagsReportLogic";
import ImportExportOptions from "components/ImportExportOptions";


const TagReport = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // API data states
  const [tagsData, setTagsData] = useState([]);
  const [uniqueTagNames, setUniqueTagNames] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  // Filter states
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = (option) => {
    setAnchorEl(null);
    if (option) {
      console.log("Export as:", option);
      // TODO: export logic
    }
  };

  // Fetch initial tags data for autocomplete options (only once)
  const fetchInitialTagsData = async () => {
    try {      
      const apiResponse = await GetTagsReport('', '', navigate);

      if (apiResponse && Array.isArray(apiResponse)) {
        // Extract unique tag names for autocomplete (keep this data persistent)
        const uniqueTags = [...new Set(apiResponse.map(item => item.tagName))]
          .filter(tag => tag) // Remove null/undefined
          .sort();
        setUniqueTagNames(uniqueTags);
        
      }
    } catch (error) {
      console.error('Error fetching initial tags data:', error);
      setUniqueTagNames([]);
    }
  };

  // Fetch filtered tags data with API call
  const fetchFilteredTagsData = async () => {
    setDataLoading(true);
    setDataError(null);
    
    try {
      // Prepare filter parameters
      const tagNames = selectedTags.length > 0 ? selectedTags.join(',') : '';
      const moduleNames = selectedModules.length > 0 ? selectedModules.join(',') : '';
      
      const apiResponse = await GetTagsReport(tagNames, moduleNames, navigate);

      if (apiResponse && Array.isArray(apiResponse)) {
        // Transform API data to match DataGrid format
        const transformedData = apiResponse.map((item, index) => ({
          id: index + 1, // Use index as ID since we don't display tagID
          tagName: item.tagName || 'N/A',
          module: item.moduleName || 'N/A',
          tagCount: item.tagCount || 0
        }));

        setTagsData(transformedData);
        
      } else {
        console.warn('Invalid API response format');
        setTagsData([]);
      }
    } catch (error) {
      console.error('Error fetching filtered tags data:', error);
      setDataError('Failed to fetch tags data. Please try again.');
      setTagsData([]);
    } finally {
      setDataLoading(false);
    }
  };

  // Load initial data on component mount
  useEffect(() => {
    fetchInitialTagsData();
    fetchFilteredTagsData(); // Load all data initially
  }, []);

  // Handle filter changes
  const handleTagsChange = (event, newValue) => {
    if (newValue && newValue.includes('All')) {
      // If "All" is selected, select all available tags (excluding "All" itself)
      if (selectedTags.length === uniqueTagNames.length) {
        // If all tags are already selected, deselect all
        setSelectedTags([]);
      } else {
        // Select all available tags
        setSelectedTags([...uniqueTagNames]);
      }
    } else {
      // Normal selection without "All"
      setSelectedTags(newValue || []);
    }
  };

  const handleModulesChange = (event) => {
    const value = event.target.value;
    const moduleOptions = ['Customer', 'Employee', 'Request', 'Assessment', 'Estimate', 'WorkOrder'];
    
    if (value.includes('all')) {
      // If "All" is selected, select all options or deselect all
      if (selectedModules.length === moduleOptions.length) {
        setSelectedModules([]);
      } else {
        setSelectedModules([...moduleOptions]);
      }
    } else {
      setSelectedModules(value);
    }
  };

  // Handle run filter button
  const handleRunFilter = () => {
    fetchFilteredTagsData();
  };

  // Sample tag data based on the image
  const rows = [
    {
      id: 1,
      tagName: "Priority Customer",
      module: "Customer",
      tagCount: 12,
    },
    {
      id: 2,
      tagName: "Seasonal Jobs",
      module: "Estimate",
      tagCount: 5,
    },
    {
      id: 3,
      tagName: "Project Type A",
      module: "Work Order",
      tagCount: 5,
    },
    {
      id: 4,
      tagName: "Warranty Work",
      module: "Job",
      tagCount: 15,
    },
  ];

  // DataGrid columns
  const columns = [
    {
      field: "tagName",
      headerName: "Tag Name",
      flex: 1,
    },
    {
      field: "module",
      headerName: "Module",
      flex: 1,
    },
    {
      field: "tagCount",
      headerName: "Tag Count",
      flex: 1,
    },
  ];

  const handleNavigation = (to) => {
    navigate(to);
  };

  const handlePageBack = () => {
    handleNavigation("/reports");
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Tag Report"
    >
      <Card
        sx={{
          p: 3,
          boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          height: "100%",
          width: "100%",
          overflowX: "auto",
          mx: "auto",
          mt: 4,
        }}
      >
        <CardContent>
          {/* Header */}
          <Box
            sx={{
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              p: 3,
              bgcolor: "white",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h4">Tags</Typography>
                <Typography variant="body1" color="text.secondary">
                  Export reports grouped by custom tags
                </Typography>
              </Box>
              <ImportExportOptions 
                handleOpenBulkImportDlg={()=>{}}                          
                data={tagsData}
                module="Tags"
              />
            </Box>
            <Divider sx={{ mb: 5 }} />

            {/* Filters */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "#F8FAFC", mt: 2 }}
              >
                <Typography variant="h5">Filters</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: "#F8FAFC" }}>
                {/* First Row */}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    // justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                 <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <CustomAutocomplete
                        multiple
                        size="small"
                        options={['All', ...uniqueTagNames]}
                        value={selectedTags}
                        onChange={handleTagsChange}
                        getOptionLabel={(option) => option}
                        renderOption={(props, option) => {
                          // For the "All" option
                          if (option === 'All') {
                            return (
                              <MenuItem 
                                {...props}
                                key="all"
                                sx={{
                                  backgroundColor: props['aria-selected'] ? 'action.selected' : 'transparent',
                                }}
                              >
                                <Checkbox 
                                  checked={selectedTags.length === uniqueTagNames.length}
                                  indeterminate={selectedTags.length > 0 && selectedTags.length < uniqueTagNames.length}
                                />
                                <ListItemText 
                                  primary="All" 
                                  sx={{
                                    '& .MuiTypography-root': {
                                      fontSize: '0.875rem',
                                      fontWeight: 'bold',
                                    }
                                  }}
                                />
                              </MenuItem>
                            );
                          }
                          return (
                            <MenuItem 
                              {...props}
                              key={option}
                              sx={{
                                backgroundColor: props['aria-selected'] ? 'action.selected' : 'transparent',
                                pl: 3, 
                              }}
                            >
                              <Checkbox checked={selectedTags.includes(option)} />
                              <ListItemText 
                                primary={option} 
                                sx={{
                                  '& .MuiTypography-root': {
                                    fontSize: '0.875rem',
                                  }
                                }}
                              />
                            </MenuItem>
                          );
                        }}
                        renderTags={(value, getTagProps) => {
                          const numTags = value.length;
                          const limitTags = 1;

                          return (
                            <>
                              {value.slice(0, limitTags).map((option, index) => (
                                <span key={index} {...getTagProps({ index })}>
                                  {option}
                                </span>
                              ))}
                              {numTags > limitTags && (
                                <span style={{ 
                                  fontSize: '0.875rem', 
                                  color: theme.palette.text.secondary,
                                  marginLeft: '4px'
                                }}>
                                  +{numTags - limitTags} more
                                </span>
                              )}
                            </>
                          );
                        }}
                        renderInput={(params) => (
                          <CustomTextField
                            {...params}
                            label="Tag Name"
                            placeholder={selectedTags.length > 0 ? "" : "Select Tags"}
                            size="small"
                            sx={{
                              width: "300px !important",
                              "& .MuiInputBase-root": {
                                width: "100% !important",
                                border: "1px solid #D9D9D9",
                                boxShadow: "none",
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "#1B2431"
                                    : "#fff !important",
                              },
                            }}
                          />
                        )}
                        sx={{
                          width: "300px !important",
                        }}
                      />
                    </Box>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <CustomTextField
                      select
                      size="small"
                      label="Module Name"
                      value={selectedModules}
                      onChange={handleModulesChange}
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected || selected.length === 0) {
                            return "Select Module";
                          }
                          const moduleOptions = ['Customer', 'Employee', 'Request', 'Assessment', 'Estimate', 'WorkOrder'];
                          if (selected.length === moduleOptions.length) {
                            return "Selected";
                          }
                          if (selected.length === 1) {
                            return selected[0];
                          }
                          return `${selected.length} modules selected`;
                        },
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 220,
                              width: 300,
                            }
                          }
                        }
                      }}
                      sx={{
                        width: "300px !important",
                        "& .MuiInputBase-root": {
                          width: "100% !important",
                          border: "1px solid #D9D9D9",
                          boxShadow: "none",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1B2431"
                              : "#fff !important",
                        },
                      }}
                    >
                      <MenuItem key="all" value="all">
                        <Checkbox checked={selectedModules.length === 6} />
                        <ListItemText 
                          primary="All" 
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                            }
                          }}
                        />
                      </MenuItem>
                      <MenuItem value="Customer">
                        <Checkbox checked={selectedModules.includes('Customer')} />
                        <ListItemText 
                          primary="Customer" 
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                            }
                          }}
                        />
                      </MenuItem>
                      <MenuItem value="Employee">
                        <Checkbox checked={selectedModules.includes('Employee')} />
                        <ListItemText 
                          primary="Employee" 
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                            }
                          }}
                        />
                      </MenuItem>
                      <MenuItem value="Request">
                        <Checkbox checked={selectedModules.includes('Request')} />
                        <ListItemText 
                          primary="Request" 
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                            }
                          }}
                        />
                      </MenuItem>
                      <MenuItem value="Assessment">
                        <Checkbox checked={selectedModules.includes('Assessment')} />
                        <ListItemText 
                          primary="Assessment" 
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                            }
                          }}
                        />
                      </MenuItem>
                      <MenuItem value="Estimate">
                        <Checkbox checked={selectedModules.includes('Estimate')} />
                        <ListItemText 
                          primary="Estimate" 
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                            }
                          }}
                        />
                      </MenuItem>
                      <MenuItem value="WorkOrder">
                        <Checkbox checked={selectedModules.includes('WorkOrder')} />
                        <ListItemText 
                          primary="WorkOrder" 
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                            }
                          }}
                        />
                      </MenuItem>
                    </CustomTextField>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {/* <CustomTextField
                        select
                        size="small"
                        label="Customer Type"
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) => {
                            if (!selected) {
                              return "Select Type"; 
                            }
                            const optionMap = {
                              individual: "Individual",
                              business: "Business",
                            };
                            return optionMap[selected] || selected;
                          },
                        }}
                        sx={{
                          width: "300px !important",
                          "& .MuiInputBase-root": {
                            width: "100% !important",
                            border: "1px solid #D9D9D9",
                            boxShadow: "none",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1B2431"
                                : "#fff !important",
                          },
                          "& .MuiOutlinedInput-root": {
                            width: "100% !important",
                          },
                          "& .MuiInputBase-input": {
                            width: "100% !important",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>Select Type</em> {/* placeholder option */}
                    {/* </MenuItem>
                        <MenuItem value="individual">Individual</MenuItem>
                        <MenuItem value="business">Business</MenuItem> */}
                    {/* </CustomTextField>  */}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",

                    justifyContent: "flex-end",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    variant="outlined"
                    color="main"
                    size="small"
                    onClick={handleRunFilter}
                    disabled={dataLoading}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    {dataLoading ? 'Loading...' : 'Run Filter'}
                  </Button>
                </Box>

                {/* Third Row - Run Filter button aligned right */}
              </AccordionDetails>
            </Accordion>

            {/* DataGrid */}
            <Box sx={{ mt: 5 }}>
              <Box sx={{ height: 300, width: "60%", mt: 5 }}>
                <DataGridPro
                  rows={tagsData}
                  columns={columns}
                  loading={dataLoading}
                  disableRowSelectionOnClick
                  // pageSizeOptions={[5, 10, 50]}
                  // initialState={{
                  //   pagination: { paginationModel: { pageSize: 5 } },
                  // }}
                  slots={{
                    footer: () => null
                  }}
                  sx={{
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textTransform: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      textTransform: "none",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </UserLayout>
  );
};

export default TagReport;
